import { useQuery, useMutation } from "@tanstack/react-query";
import { fetchDtfPricing } from "./dtfPricingService";
import type { DtfArtworkPricing } from "./types";

type UseTransferPricingParams = {
  transferTypeId: string | null;
  artworkId: string;
  width: number;
  height: number;
  quantity: number;
  enhancementTier: string;
  hasExpertEditAddon?: boolean;
  enabled?: boolean;
};

/**
 * Hook to fetch DTF pricing for a single transfer size configuration
 * Automatically fetches pricing when parameters change
 */
export function useTransferPricing(params: UseTransferPricingParams) {
  const {
    transferTypeId,
    artworkId,
    width,
    height,
    quantity,
    enhancementTier,
    hasExpertEditAddon = false,
    enabled = true
  } = params;

  return useQuery<DtfArtworkPricing | null>({
    queryKey: [
      "transferPricing",
      transferTypeId,
      artworkId,
      width,
      height,
      quantity,
      enhancementTier,
      hasExpertEditAddon
    ],
    queryFn: async () => {
      if (!transferTypeId || width <= 0 || height <= 0 || quantity <= 0) {
        return null;
      }

      const result = await fetchDtfPricing({
        transferTypeId,
        artworks: [
          {
            artworkId,
            width,
            height,
            quantity,
            enhancementTier,
            hasExpertEditAddon
          }
        ]
      });

      return result?.[artworkId] || null;
    },
    enabled: enabled && !!transferTypeId && width > 0 && height > 0 && quantity > 0,
    staleTime: 5 * 60 * 1000, // Consider data stale after 5 minutes
    gcTime: 10 * 60 * 1000 // Keep in cache for 10 minutes (formerly cacheTime)
  });
}

/**
 * Hook to fetch DTF pricing for one or more sizes
 * Useful when you want to trigger pricing fetch manually
 */
export function useTransferPricingMutation() {
  return useMutation<
    Record<string, DtfArtworkPricing> | null,
    Error,
    {
      transferTypeId: string;
      artworks: Array<{
        artworkId: string;
        width: number;
        height: number;
        quantity: number;
        enhancementTier: string;
        hasExpertEditAddon?: boolean;
      }>;
    }
  >({
    mutationFn: fetchDtfPricing
  });
}
